import type { IAdViewCarLeasingDetails } from "@finn-no/adview-provider-frontend-types";
import { Trans, useLingui } from "@lingui/react";
import { formatDate, formatMileage, formatNumber, formatPrice, type MileageUnit } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "./FieldValuePair.tsx";

type Props = {
  leasingDetails?: IAdViewCarLeasingDetails;
  unit: MileageUnit;
};

const LeasingInfo = ({ leasingDetails, unit }: Props) => {
  const { i18n } = useLingui();
  const { locale, brand } = useAppConfig();
  const formatedTerminationDate = formatDate(leasingDetails?.termination_date, locale);
  const formatedMileage = leasingDetails?.mileage_remaining
    ? formatMileage(leasingDetails?.mileage_remaining, unit, locale)
    : null;

  if (!leasingDetails) {
    return null;
  }

  return (
    <section className="border-t mt-40 pt-40">
      <h2 className="t3 mb-0">
        <Trans
          id="itemPageLayout.leasingInfo.title"
          message="Leasing agreement"
          comment="Title for leasing agreement section"
        />
      </h2>
      <dl className="columns-2 break-words mb-0 mt-16">
        {formatedMileage ? (
          <FieldValuePair
            field={
              brand.toLowerCase() !== "blocket"
                ? unit === "SCANDINAVIAN_MILE"
                  ? i18n.t({
                      id: "itemPageLayout.leasingInfo.remainingMileage.miles.text",
                      message: "Remaining miles in the agreement",
                    })
                  : i18n.t({
                      id: "itemPageLayout.leasingInfo.remainingMileage.text",
                      message: "Remaining kilometers in the agreement",
                    })
                : i18n.t({
                    id: "itemPageLayout.leasingInfo.yearlyMileage.text",
                    message: "Mileage per year",
                  })
            }
            value={formatedMileage}
          />
        ) : null}

        {leasingDetails?.remaining_month != null ? (
          brand.toLowerCase() === "blocket" ? (
            <FieldValuePair
              field={i18n.t({
                id: "itemPageLayout.leasingInfo.contractDuration.text",
                message: "Contract duration",
              })}
              value={i18n.t({
                id: "itemPageLayout.keyInfo.warranty.duration.postfix",
                message: "{duration, plural, one {{formattedDuration} month} other {{formattedDuration} months}}",
                comment: "Warranty duration postfix in ad specification (singular/plural)",
                values: {
                  duration: leasingDetails.remaining_month,
                  formattedDuration: formatNumber(leasingDetails.remaining_month, locale),
                },
              })}
            />
          ) : (
            <FieldValuePair
              field={i18n.t({
                id: "itemPageLayout.leasingInfo.remainingMonth.text",
                message: "Number of terms until the agreement expires",
              })}
              value={leasingDetails.remaining_month}
            />
          )
        ) : null}

        {formatedTerminationDate ? (
          <FieldValuePair
            field={i18n.t({ id: "itemPageLayout.leasingInfo.terminationDate.text", message: "Lease termination date" })}
            value={formatedTerminationDate}
            suppressHydrationWarning
          />
        ) : null}

        {leasingDetails?.change_renter_fee != null ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.changeRenterFee.text",
              message: "Fee for change of debtor",
            })}
            value={formatPrice(leasingDetails.change_renter_fee, locale, brand)}
          />
        ) : null}

        {leasingDetails?.company ? (
          <FieldValuePair
            field={i18n.t({ id: "itemPageLayout.leasingInfo.company.text", message: "Leasing company" })}
            value={leasingDetails.company}
          />
        ) : null}

        {leasingDetails?.return_location ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.returnLocation.text",
              message: "Return location for the vehicle",
            })}
            value={leasingDetails.return_location}
          />
        ) : null}
      </dl>
    </section>
  );
};

export default LeasingInfo;
